import type { CornerPoints } from "../../types/jscanify";

export interface BasicCropRegion {
  sx: number;
  sy: number;
  sourceWidth: number;
  sourceHeight: number;
  outputWidth: number;
  outputHeight: number;
}

const clamp = (value: number, min: number, max: number): number =>
  Math.min(Math.max(value, min), max);

export const calculateBasicCropRegion = (
  corners: CornerPoints,
  imageWidth: number,
  imageHeight: number,
  maxOutputDimension = 2000,
  minOutputDimension = 10,
): BasicCropRegion | null => {
  if (
    imageWidth <= 0 ||
    imageHeight <= 0 ||
    maxOutputDimension <= 0 ||
    minOutputDimension <= 0
  ) {
    return null;
  }

  const points = [
    corners.topLeftCorner,
    corners.topRightCorner,
    corners.bottomRightCorner,
    corners.bottomLeftCorner,
  ];

  const minX = clamp(
    Math.floor(Math.min(...points.map((point) => point.x))),
    0,
    imageWidth,
  );
  const maxX = clamp(
    Math.ceil(Math.max(...points.map((point) => point.x))),
    0,
    imageWidth,
  );
  const minY = clamp(
    Math.floor(Math.min(...points.map((point) => point.y))),
    0,
    imageHeight,
  );
  const maxY = clamp(
    Math.ceil(Math.max(...points.map((point) => point.y))),
    0,
    imageHeight,
  );

  const sourceWidth = maxX - minX;
  const sourceHeight = maxY - minY;

  if (sourceWidth < minOutputDimension || sourceHeight < minOutputDimension) {
    return null;
  }

  const maxSourceDimension = Math.max(sourceWidth, sourceHeight);
  const scale =
    maxSourceDimension > maxOutputDimension
      ? maxOutputDimension / maxSourceDimension
      : 1;

  return {
    sx: minX,
    sy: minY,
    sourceWidth,
    sourceHeight,
    outputWidth: Math.max(minOutputDimension, Math.round(sourceWidth * scale)),
    outputHeight: Math.max(
      minOutputDimension,
      Math.round(sourceHeight * scale),
    ),
  };
};

export const createBasicDocumentCropCanvas = (
  sourceCanvas: HTMLCanvasElement,
  corners: CornerPoints,
): HTMLCanvasElement | null => {
  const region = calculateBasicCropRegion(
    corners,
    sourceCanvas.width,
    sourceCanvas.height,
  );

  if (!region) {
    return null;
  }

  const resultCanvas = document.createElement("canvas");
  resultCanvas.width = region.outputWidth;
  resultCanvas.height = region.outputHeight;

  const ctx = resultCanvas.getContext("2d");
  if (!ctx) {
    return null;
  }

  ctx.drawImage(
    sourceCanvas,
    region.sx,
    region.sy,
    region.sourceWidth,
    region.sourceHeight,
    0,
    0,
    region.outputWidth,
    region.outputHeight,
  );

  return resultCanvas;
};
